type ListCacheEntry = {
    items: unknown[];
    totalCount: number;
    timestamp: number;
};

export type ListCacheParams = Record<string, unknown>;

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, ListCacheEntry>();

export function buildListCacheKey(page: string, params: ListCacheParams = {}): string {
    return `${page}:${JSON.stringify(params)}`;
}

export function readListCache(key: string): ListCacheEntry | null {
    const entry = cache.get(key);
    if (!entry) {
        return null;
    }
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(key);
        return null;
    }
    return entry;
}

export function writeListCache(key: string, items: unknown[], totalCount: number): void {
    cache.set(key, { items, totalCount, timestamp: Date.now() });
}

export function invalidateListCache(key: string): void {
    cache.delete(key);
}

export function prependListCacheItem(
    pageId: string,
    params: ListCacheParams,
    item: unknown
): void {
    if (!item || typeof item !== "object") {
        return;
    }

    const itemId = (item as { id?: string | number }).id;
    const cacheKey = buildListCacheKey(pageId, params);
    const cached = readListCache(cacheKey);
    const existingItems = (cached?.items ?? []) as Array<{ id?: string | number }>;
    const nextItems =
        itemId == null
            ? [item, ...existingItems]
            : [item, ...existingItems.filter((entry) => String(entry.id) !== String(itemId))];

    writeListCache(cacheKey, nextItems, nextItems.length);
}

export function invalidateListCacheByPage(pageId: string): void {
    const prefix = `${pageId}:`;
    cache.forEach((_, key) => {
        if (key.startsWith(prefix)) {
            cache.delete(key);
        }
    });
}

export function getListCacheBootstrap(pageId: string, params: ListCacheParams = {}) {
    const cacheKey = buildListCacheKey(pageId, params);
    const cached = readListCache(cacheKey);
    return {
        cacheKey,
        cached,
        hasCache: Boolean(cached),
        items: cached?.items ?? [],
        totalCount: cached?.totalCount ?? 0,
    };
}

export function isListFullyFetched(itemsLength: number, totalCount: number, pageSize: number): boolean {
    return itemsLength < pageSize || itemsLength >= totalCount;
}

/** Bottom-row skeletons are for infinite scroll only, not page-1 refetch/sort. */
export function isInfiniteScrollLoading(isLoading: boolean, isInitialLoad: boolean): boolean {
    return isLoading && !isInitialLoad;
}
